import {
  call,
  put,
  takeLatest,
  takeEvery,
  takeLeading,
  all,
  fork,
  delay,
  select
} from 'redux-saga/effects'

import {
  getUsers,
  delUser,
  getTenantUsers,
  inviteUserApi,
  postUsers,
  getProject
} from '../apis/admin'

import {
  LOAD_USERS,
  LoadUsersType,
  UPDATE_USER,
  DELETE_USERS,
  loadUsers,
  InviteUserType,
  INVITE_USER,
  UpdateUserType,
  DeleteUsersType,
  inviteUserSuccess,
  inviteUserError,
  LOAD_MEMBER_REPORT,
  LoadMemberReportType
} from '../actions/users'
import { JOB_STATES, getJob, requestMemberReport } from '../apis/memberReport'
import { getProjectDatasets, getProjectFolders } from '../apis/project'
import { delProject } from '../apis/projects'
import { purgeRecycleBinProject } from '../apis/recycleBin'
import {
  TRANSFER_STATUSES,
  getTransfer,
  transferDownloadConvert
} from '../apis/transfer'
import { mapUserDownloadDataToConvertApiBody } from '../helpers/import'
import { fetchText } from '../helpers/download'
import {
  cacheMemberReport,
  readCachedMemberReport
} from '../helpers/memberReport'
import asIfReaderWriter from '../components/DatasetConversionDialog/model/asIfReaderWriter'

import { addError } from '../actions/errors'

import { notify } from '../helpers/saga'

import ITenantUser from '../model/ITenantUser'
import { getMemberReportDialogOpen } from '../state'
import { setUsers, setLoadingUsers, setMemberReport } from '../reducers/users'

export const MEMBER_REPORT_POLL_DELAY = 2000
// The job takes a few seconds on dev; give up after ~2 minutes.
export const MEMBER_REPORT_MAX_POLLS = 60

export default function* watchUsers() {
  yield takeEvery(UPDATE_USER, updateUser)
  yield takeEvery(DELETE_USERS, deleteUsers)
  yield takeLatest(LOAD_USERS, fetchUsers)
  yield takeEvery(INVITE_USER, inviteUser)
  yield takeLeading(LOAD_MEMBER_REPORT, loadMemberReport)
}

export function* fetchUsers(action: LoadUsersType) {
  try {
    yield put(setLoadingUsers({ loadingUsers: true }))
    const tenantUsers = (yield call(getTenantUsers)).users
    const users = (yield call(getUsers, action.tenantId)).data
    const filteredUsers = users.filter(u => u.principalType === 'UserIdentity')
    const tUsers: ITenantUser[] = filteredUsers.map(user => {
      const tUser = tenantUsers.find(
        tu =>
          tu.userIdentityId === user.userId &&
          user.principalType === 'UserIdentity'
      )

      return {
        id: tUser?.userIdentityId ?? user.userId,
        email: tUser?.email ?? '',
        name: tUser?.name ?? '',
        role: user?.role
      }
    })
    yield put(setUsers({ users: tUsers }))
  } catch (error) {
    yield put(addError(LOAD_USERS, error))
  } finally {
    yield put(setLoadingUsers({ loadingUsers: false }))
  }
}

export function* updateUser(action: UpdateUserType) {
  try {
    yield put(setLoadingUsers({ loadingUsers: true }))
    const project = yield call(getProject, action.tenantId)

    const users = project.members.map(user => {
      if (user.userId === action.user.id) {
        return action.user
      }
      return {
        id: user.userId,
        role: user.role
      }
    })

    yield call(postUsers, users, project.rowVersion, action.tenantId)
    yield put(loadUsers(action.tenantId))

    yield fork(notify, `User "${action.user.name}" was updated!` as any)
  } catch (error) {
    yield put(addError(UPDATE_USER, error))
  } finally {
    yield put(setLoadingUsers({ loadingUsers: false }))
  }
}

export function* deleteUsers(action: DeleteUsersType) {
  const { users, tenantId } = action

  try {
    yield all(users.map(user => call(delUser, user.id, tenantId)))

    yield put(loadUsers(tenantId))
    yield fork(
      notify,
      users.length > 1
        ? `${users.length} users were deleted!`
        : ('The user was deleted!' as any)
    )
  } catch (error) {
    yield put(addError(DELETE_USERS, error))
  }
}

// export function* inviteUser(action: InviteUserType) {
//   try {
//     yield call(
//       inviteUserApi,
//       action.email,
//       action.role,
//       action.redirectUri,
//       action.tenantId
//     )
//   } catch (error) {
//     yield put(addError(INVITE_USER, error))
//   }
// }

export function* inviteUser(action: InviteUserType) {
  try {
    yield call(
      inviteUserApi,
      action.email,
      action.role,
      action.redirectUri,
      action.tenantId
    )

    yield put(inviteUserSuccess(action.email))
    yield fork(notify, `Invitation sent successfully to ${action.email}`)
  } catch (error) {
    yield put(inviteUserError(error))
    yield put(addError(INVITE_USER, error))
  }
}

export function* waitForJob(jobId: string) {
  for (let i = 0; i < MEMBER_REPORT_MAX_POLLS; i++) {
    const job = yield call(getJob, jobId)
    if (job.jobState === JOB_STATES.FINISHED) {
      if (job.hasError) {
        throw new Error(job.statusMessage || 'Member report job failed')
      }
      return
    }
    yield delay(MEMBER_REPORT_POLL_DELAY)
  }
  throw new Error('Member report job timed out')
}

// The transfer's downloadPath is a signed link to the report file itself, not
// a copy, so the file is pulled into memory before its folder gets deleted.
export function* fetchReport(datasetId: string) {
  const transfer = yield call(
    transferDownloadConvert,
    datasetId,
    mapUserDownloadDataToConvertApiBody({
      importData: { name: 'member-report.csv', ...asIfReaderWriter }
    })
  )

  for (let i = 0; i < MEMBER_REPORT_MAX_POLLS; i++) {
    const info = yield call(getTransfer, transfer.id)
    if (info.status === TRANSFER_STATUSES.COMPLETED) {
      return yield call(fetchText, info.downloadPath)
    }
    if (info.status === TRANSFER_STATUSES.ERROR) {
      throw new Error(info.errorMessage || 'Member report download failed')
    }
    yield delay(MEMBER_REPORT_POLL_DELAY)
  }
  throw new Error('Member report download timed out')
}

function* findReportFolderId(tenantId: string, folderName: string) {
  const { data: folders } = yield call(
    getProjectFolders,
    tenantId,
    0,
    10,
    'UpdatedAt',
    'Desc',
    folderName
  )
  return folders.find(folder => folder.name === folderName)?.id
}

// The backend job writes the report into a folder of our choosing, so each
// load gets a uniquely named folder: the report is the only file in it, and
// the whole folder is removed afterwards (the report lists every user's email
// and role, so it shouldn't linger in the tenant or the recycle bin).
export function* loadMemberReport(action: LoadMemberReportType) {
  const { tenantId, refresh } = action
  if (!refresh) {
    const cached = yield call(readCachedMemberReport, tenantId)
    if (cached) {
      yield put(setMemberReport({ status: 'loaded', ...cached }))
      return
    }
  }

  const folderName = `member-report-${new Date()
    .toISOString()
    .replace(/[:.]/g, '-')}`
  let folderId: string | undefined
  let requested = false

  try {
    yield put(setMemberReport({ status: 'loading' }))

    const { jobId } = yield call(requestMemberReport, tenantId, folderName)
    requested = true
    yield call(waitForJob, jobId)

    folderId = yield* findReportFolderId(tenantId, folderName)
    if (!folderId) {
      throw new Error('Member report folder not found')
    }

    const [report] = yield call(getProjectDatasets, folderId)
    if (!report) {
      throw new Error('Member report file not found')
    }

    const csv = yield call(fetchReport, report.id)
    const generatedAt = new Date().toISOString()
    const dialogOpen = yield select(getMemberReportDialogOpen)
    yield put(
      setMemberReport({
        status: 'loaded',
        csv,
        generatedAt,
        ready: !dialogOpen
      })
    )
    yield call(cacheMemberReport, tenantId, { csv, generatedAt })
  } catch (error) {
    yield put(setMemberReport({ status: 'error' }))
    yield put(addError(LOAD_MEMBER_REPORT, error))
  } finally {
    try {
      // A failed job may have written its folder before failing. After a
      // timeout this only catches a folder already there: one the job writes
      // later is left to the backend to clean up.
      if (!folderId && requested) {
        folderId = yield* findReportFolderId(tenantId, folderName)
      }
      if (folderId) {
        yield call(delProject, folderId)
        yield call(purgeRecycleBinProject, folderId)
      }
    } catch (error) {
      yield put(addError(LOAD_MEMBER_REPORT, error))
    }
  }
}
